/**
 * Opaque, versioned identity for an anonymous public-template URL.
 *
 * `emails.emailId` is tenant-controlled and is not globally unique. The
 * preview mirror, however, has one globally unique Convex document id per
 * live public tuple. Exposing that opaque id gives public links a stable
 * collision-free identity without encoding the owning org or brand in the
 * URL.
 *
 * The prefix reserves room for future formats. A prefixed-but-invalid value
 * must never fall through to the legacy email-id path: that would let a
 * malformed exact reference silently select a different tenant's row.
 */

const PUBLIC_TEMPLATE_REFERENCE_PREFIX = 'pt1_'
const PUBLIC_TEMPLATE_REFERENCE_MAX_LENGTH = 256

export type ParsedPublicTemplateReference =
  | { kind: 'preview'; previewId: string }
  | { kind: 'legacy'; emailId: string }
  | { kind: 'invalid' }

/**
 * `emailImageAnalyses.orgId` for a manifest analyzed over a PUBLIC template
 * donor. The public detail projection deliberately hides the source row's
 * org (the #1168 BOLA hardening), and that column is only the org-deletion
 * cascade key — so public donors cache under this sentinel instead of never
 * caching at all (a flow remix re-ran the vision pre-pass for every step of
 * every remix, and each run was one gateway timeout away from no manifest).
 */
export const PUBLIC_TEMPLATE_MANIFEST_CACHE_ORG = 'public-templates'

export function createPublicTemplateReference(previewId: string): string {
  return `${PUBLIC_TEMPLATE_REFERENCE_PREFIX}${previewId}`
}

export function parsePublicTemplateReference(
  value: unknown
): ParsedPublicTemplateReference {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > PUBLIC_TEMPLATE_REFERENCE_MAX_LENGTH
  ) {
    return { kind: 'invalid' }
  }
  if (!value.startsWith(PUBLIC_TEMPLATE_REFERENCE_PREFIX)) {
    return { kind: 'legacy', emailId: value }
  }

  const previewId = value.slice(PUBLIC_TEMPLATE_REFERENCE_PREFIX.length)
  return previewId.length > 0
    ? { kind: 'preview', previewId }
    : { kind: 'invalid' }
}

/** Rolling-deploy adapter for projections emitted before `publicReference`. */
export function publicTemplateReferenceOf(template: {
  emailId: string
  publicReference?: string | undefined
}): string {
  return template.publicReference ?? template.emailId
}
